import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { compact, get, isArray, uniq } from 'lodash';
import { Tooltip, Typography } from '@mui/material';
import PlayIcon from '@mui/icons-material/PlayCircleFilled';

import ManageTestsSearchForm from './ManageTestsSearchForm';

import Header from 'components/Header/Header';

import { findTranslation } from 'utils/grid/translate';
import { useLanguage } from 'utils/hooks/useLanguage';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { useTests } from 'utils/hooks/useTests';

import { useWithTitle } from 'utils/hooks/useWithTitle';
import { useMuiGrid } from 'utils/hooks/useMuiGrid';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router';
import { encryptId } from 'utils/hooks/useApp';
import { TourTests } from './TourTests';
import { MuiGrid } from 'components/MuiGrid/MuiGrid';
import { format } from 'date-fns';

const muiGridKey = 'startTestResultsMui';

const StartTest: React.FC = () => {
  const { t } = useTranslation('Tests');
  const { currentLocale } = useLanguage();
  const { testResults } = useAppInfo();
  const navigate = useNavigate();
  const { injectColumnWidthsIntoColumns, reorderColumnsByGridSettings } = useMuiGrid(muiGridKey);

  const { triggerSubmit } = useTests(t);

  useWithTitle(); // sets title to document

  const onCustomEntityStart = useCallback(
    (row: any) =>
      navigate(
        `/tests/startTest/${encryptId(get(row, 'id'))}${
          get(row, 'testIsUnfinished', false) ? '/1/0' : ''
        }`,
      ),
    [navigate],
  );

  const { testResultsModified, uniqTypes } = useMemo(() => {
    if (!isArray(testResults)) {
      return {
        testResultsModified: null,
        uniqTypes: [],
      };
    }

    const allTypes: string[] = [];
    const newTestResults = testResults.map((item) => {
      const typeTranslated = findTranslation(
        currentLocale,
        'Tests',
        get(item, 'type', '').toLowerCase(),
      );
      allTypes.push(typeTranslated);

      return {
        ...item,
        typeTranslated,
        maxTestLengthModified: new Date(`1970-01-01T${get(item, 'maxTestLength')}`),
      };
    });

    return {
      testResultsModified: newTestResults,
      uniqTypes: uniq(compact(allTypes)),
    };
  }, [currentLocale, testResults]);

  const columns = useMemo(
    () =>
      reorderColumnsByGridSettings(
        injectColumnWidthsIntoColumns([
          {
            field: 'actions',
            headerName: t('Grid:actions'),
            type: 'actions',
            hideable: false,
            width: 185,
            headerAlign: 'left',
            align: 'left',
            getActions: ({ row }: GridRowParams) => [
              <GridActionsCellItem
                icon={
                  <Tooltip
                    title={
                      get(row, 'testIsUnfinished', false)
                        ? t('Grid:continueTest')
                        : t('Grid:testStart')
                    }
                  >
                    <PlayIcon />
                  </Tooltip>
                }
                label="Start"
                onClick={() => onCustomEntityStart(row)}
              />,
            ],
          },

          {
            field: 'name',
            headerName: t('name'),
          },
          {
            field: 'description',
            headerName: t('description'),
          },
          {
            field: 'typeTranslated',
            headerName: t('type'),
            type: 'singleSelect',
            valueOptions: uniqTypes,
          },
          {
            field: 'maxTestLengthModified',
            headerName: t('maxTestLength'),
            valueGetter: (value: any, row: any) => value && new Date(value),
            valueFormatter: (value: any) => value && `${format(value, 'HH:mm:ss')} ${t('hrs')}`,
          },
        ]),
      ),
    [
      injectColumnWidthsIntoColumns,
      onCustomEntityStart,
      reorderColumnsByGridSettings,
      t,
      uniqTypes,
    ],
  );

  const renderedSteps = () => {
    return <TourTests type="start" />;
  };

  return (
    <>
      <Typography component="div">
        <Header title={t('startTest')} TourComponent={renderedSteps()} />
        <ManageTestsSearchForm
          forStartTest={true}
          shouldShowStateField={false}
          triggerSubmit={triggerSubmit}
        />
      </Typography>
      <MuiGrid
        gridKey={muiGridKey}
        rows={testResultsModified || []}
        columns={columns}
        dataTour="testsStartGrid"
      />
    </>
  );
};

export default StartTest;
